/**
 * Creating an account and signing in from the partner redeem page (#2357). These are the same
 * requests SignUp.jsx and Login.js send (both left as they are):
 *  - sign-up: auth-api/signUp/account -> signUp/accountDetails -> signUp/activateAccount
 *  - sign-in: auth-api/signIn -> encryption/encrypt -> rolePermissions (property owner portal)
 * Both start with a reCAPTCHA token. The backend answers a token it doesn't trust with FORBIDDEN,
 * after which the pages show a checkbox challenge and send its token with isRecaptchaV2: true.
 */
import APIClient from '../neoverv/config/APIClient';
import { config } from '../neoverv/config/Setting';

export const AUTH_ERROR = {
  CHALLENGE_REQUIRED: 'CHALLENGE_REQUIRED', // show the reCAPTCHA checkbox, then send again
  EMAIL_EXISTS: 'EMAIL_EXISTS',
  INVALID_LOGIN: 'INVALID_LOGIN',
  NO_ACCESS: 'NO_ACCESS', // signed in, but not allowed into this app (as in Login.js)
  ERROR: 'ERROR',
};

export class AuthError extends Error {
  constructor(code) {
    super(code);
    this.code = code;
  }
}

// Keys of the reCAPTCHA script public/index.html loads, and of the checkbox SignUp.jsx and
// Login.js fall back to - the backend verifies the challenge against that same key.
const RECAPTCHA_SITE_KEY = '6Lee-40sAAAAAH3KM7HK3E9rq13H69XWcptUYE2a';
const RECAPTCHA_CHALLENGE_SITE_KEY = '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';

export const getRecaptchaToken = async (action) => {
  await new Promise((resolve) => window.grecaptcha.enterprise.ready(resolve));
  return window.grecaptcha.enterprise.execute(RECAPTCHA_SITE_KEY, { action });
};

// Puts the checkbox challenge into `container`, or resets it when this container already shows
// one (`widgetId`). Returns the widget id.
export const renderRecaptchaChallenge = (container, widgetId, { onSolved, onError }) => {
  if (widgetId !== null && widgetId !== undefined) {
    window.grecaptcha.enterprise.reset(widgetId);
    return widgetId;
  }
  return window.grecaptcha.enterprise.render(container, {
    sitekey: RECAPTCHA_CHALLENGE_SITE_KEY,
    callback: onSolved,
    'error-callback': onError,
  });
};

const failureText = (data) => (data?.message || data?.code || '').toString().toUpperCase();

// A request the backend refused: an HTTP error, or a 200 whose body says { status: false }.
const refusal = (error) => (error?.response ? failureText(error.response.data) : null);

// `partnerId` links the account to the partner whose voucher brought it (#2357); redeem only
// accepts that partner's vouchers for it. `voucherId` makes the activation redeem that voucher
// for the new account. Both null for a plain hellohousing signup.
export const createAccount = async ({ email, password, recaptchaToken, isRecaptchaV2, partnerId = null, voucherId = null }) => {
  let account;
  try {
    const response = await APIClient({
      method: 'POST',
      url: config.signUpAccount,
      data: { email, recaptchaToken, agencyReferralToken: null, isRecaptchaV2, partnerId },
    });
    if (response.data?.status === false || !response.data?.result?.accountId) {
      throw Object.assign(new Error('signUp/account refused'), { response });
    }
    account = response.data.result;
  } catch (error) {
    const text = refusal(error);
    if (text === 'FORBIDDEN') throw new AuthError(AUTH_ERROR.CHALLENGE_REQUIRED);
    if (text?.includes('EXIST')) throw new AuthError(AUTH_ERROR.EMAIL_EXISTS);
    throw new AuthError(AUTH_ERROR.ERROR);
  }

  try {
    // Same details SignUp.jsx sends: the name is the e-mail's local part until the user edits it.
    await APIClient({
      method: 'PUT',
      url: config.signUpAccountDetails,
      data: { accountId: account.accountId, password, firstName: email.split('@')[0], company: '', address: '', city: '', zipCode: '' },
    });
    const activation = await APIClient({ method: 'PUT', url: config.signUpActivateAccount, data: { accountId: account.accountId, voucherId } });
    return { accountId: account.accountId, userId: account.user?.userId, activated: activation.data?.status !== false };
  } catch (error) {
    throw new AuthError(AUTH_ERROR.ERROR);
  }
};

// Login.js: the AI agent server authenticates with an encrypted copy of the token. Signing in
// works without it, as it does there.
const encryptToken = async (token) => {
  try {
    const body = new FormData();
    body.append('text', token);
    const response = await APIClient({ method: 'POST', url: config.encryptToken, data: body });
    return response.data?.result ?? null;
  } catch (error) {
    return null;
  }
};

const hasPropertyOwnerAccess = async (token) => {
  try {
    const response = await APIClient({
      method: 'GET',
      url: `${config.rolePermissions}?permission=View:PropertyOwnerPortal`,
      headers: { Authorization: token },
    });
    return response.data?.status !== false && Boolean(response.data?.result);
  } catch (error) {
    return false;
  }
};

// -> the `profile` Login.js stores with setProfileData.
export const signIn = async ({ email, password, recaptchaToken, isRecaptchaV2 }) => {
  let data;
  try {
    ({ data } = await APIClient({ method: 'POST', url: config.signIn, data: { email, password, recaptchaToken, isRecaptchaV2 } }));
  } catch (error) {
    if (!error.response || error.response.status >= 500) {
      throw new AuthError(AUTH_ERROR.ERROR);
    }
    data = error.response.data;
  }
  if (data?.status === false || !data?.result?.token) {
    // Login.js shows the same message for a wrong password and a deleted or denied account.
    throw new AuthError(failureText(data) === 'FORBIDDEN' ? AUTH_ERROR.CHALLENGE_REQUIRED : AUTH_ERROR.INVALID_LOGIN);
  }

  const user = data.result;
  const [encryptedToken, hasAccess] = await Promise.all([encryptToken(user.token), hasPropertyOwnerAccess(user.token)]);
  if (!hasAccess) {
    throw new AuthError(AUTH_ERROR.NO_ACCESS);
  }
  return {
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    address: '-',
    city: '-',
    zip: 0,
    company: '',
    phoneNumber: user.phoneNumber,
    id: user.id,
    file: '',
    propertyData: [],
    auth: { token: user.token, encryptedToken },
    accountId: user.accountId,
  };
};
